import React from "react";
import { StealthRobot, useArrivalState } from "./robot/StealthRobot";
import { Volume2, Loader2, RotateCw, Download } from "lucide-react";
import { Render3DCard } from "./render3d/Render3DCard";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Reading = ({ text }) => (text || "").split(/\n{2,}/).map((p, i) => <p key={i} className="whitespace-pre-wrap">{p}</p>);

const fmt = (iso) => {
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

function MediaBlock({ media = [] }) {
  if (!media || media.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2" data-testid="message-media">
      {media.map((m, i) => {
        if (m.kind === "image") {
          return (
            <div key={i} className="relative group rounded-xl overflow-hidden border border-white/10">
              <img src={`${BACKEND}${m.url}`} alt="attachment" className={`${m.wallpaper ? "max-h-96" : "max-h-72"} max-w-full object-cover`} data-testid="message-image" />
              <a href={`${BACKEND}${m.url}?download=1`} download data-testid="message-image-download"
                 className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
                <Download className="w-3.5 h-3.5" />
              </a>
              {m.wallpaper && (
                <a href={`${BACKEND}${m.url}?download=1`} download data-testid="wallpaper-download"
                   className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full px-3.5 py-1.5 text-xs text-[#000000] flex items-center gap-1.5 shadow-lg whitespace-nowrap"
                   style={{ background: "linear-gradient(90deg,#FFFFFF,#C8C8C8)" }}>
                  <Download className="w-3.5 h-3.5" /> Download wallpaper
                </a>
              )}
            </div>
          );
        }
        if (m.kind === "render3d") return <Render3DCard key={i} jobId={m.job_id} />;
        if (m.kind === "video") {
          if (m.status === "generating" || !m.url) {
            return (
              <div key={i} data-testid="message-video-loading" className="rounded-xl border border-white/10 bg-white/5 w-64 h-40 flex flex-col items-center justify-center gap-2 text-sm text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin text-mono-300" /> Generating video…
              </div>
            );
          }
          if (m.status === "failed") {
            return <div key={i} className="rounded-xl border border-red-400/30 bg-red-400/5 w-64 p-4 text-sm text-red-300">Video generation failed — you weren't charged.</div>;
          }
          return <video key={i} src={`${BACKEND}${m.url}`} controls className="rounded-xl border border-white/10 max-h-72 max-w-full" data-testid="message-video" />;
        }
        return (
          <a key={i} href={`${BACKEND}${m.url}`} target="_blank" rel="noreferrer"
             className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-slate-300 hover:text-mono-300">
            {m.name || "file"}
          </a>
        );
      })}
    </div>
  );
}

function AssistantEntry({ m, onReplay, status, isLast, isFirst, liveState }) {
  const arrival = useArrivalState(!!m.fresh, isFirst);
  // Voice playback drives "speaking"; fetching the voice shows "thinking"; mic open on the latest reply shows "listening".
  const robotState = status === "playing" ? "speaking" : status === "generating" ? "thinking"
    : isLast && liveState === "listening" ? "listening" : arrival;
  const isAnna = (m.assistant || "anna") === "anna";
  const name = isAnna ? "Anna" : "Sam";
  const accent = isAnna ? "#E4E4E4" : "#D6D6D6";
  const busy = status === "generating" || status === "playing";
  const failed = status === "failed";
  return (
    <div className="flex gap-3 fade-in-up" data-testid="stream-assistant-entry">
      <StealthRobot size={46} state={robotState} testid="assistant-robot" className="-mt-1" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-heading text-sm font-medium" style={{ color: accent }}>{name}</span>
          <span className="w-1 h-1 rounded-full bg-slate-600" />
          <span className="text-[11px] text-slate-500">{fmt(m.created_at)}</span>
          <button
            data-testid="stream-replay-button"
            data-tts-status={status || "idle"}
            onClick={() => onReplay(m)}
            className={`ml-1 transition-colors ${failed ? "text-red-400 hover:text-red-300" : "text-slate-500 hover:text-mono-300"}`}
            title={failed ? "Voice failed — tap to retry" : busy ? "Speaking…" : "Replay voice"}
          >
            {status === "generating" ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : failed ? <RotateCw className="w-3.5 h-3.5" />
              : <Volume2 className={`w-3.5 h-3.5 ${status === "playing" ? "text-mono-300 animate-pulse" : ""}`} />}
          </button>
        </div>
        {m.text && (
          <div className="mt-1.5 pl-3 border-l-2 text-[15px] text-slate-100 reading" style={{ borderColor: `${accent}66` }} data-testid="assistant-message-text">
            <Reading text={m.text} />
          </div>
        )}
        <MediaBlock media={m.media} />
      </div>
    </div>
  );
}

function UserEntry({ m }) {
  return (
    <div className="flex justify-end fade-in-up" data-testid="stream-user-entry">
      <div className="max-w-[80%]">
        <div className="flex items-center gap-2 justify-end">
          <span className="text-[11px] text-slate-500">{fmt(m.created_at)}</span>
          <span className="label-mono !text-mono-300/70">You</span>
        </div>
        {m.text && (
          <div className="mt-1.5 rounded-2xl rounded-tr-sm px-4 py-2.5 text-[15px] text-slate-100 reading"
               style={{ background: "rgba(235,235,235,0.10)", border: "1px solid rgba(235,235,235,0.15)" }}>
            {m.text}
          </div>
        )}
        <div className="flex justify-end"><MediaBlock media={m.media} /></div>
      </div>
    </div>
  );
}

export function ConversationStream({ messages, onReplay, speakingId, statusById = {}, thinking, assistant, liveState }) {
  const replies = messages.filter((m) => m.role !== "user");
  const firstId = replies[0]?.id, lastId = replies[replies.length - 1]?.id;
  return (
    <div className="space-y-6" data-testid="conversation-stream">
      {messages.map((m) => (
        m.role === "user"
          ? <UserEntry key={m.id} m={m} />
          : <AssistantEntry key={m.id} m={m} onReplay={onReplay} status={statusById[m.id]} isFirst={m.id === firstId} isLast={!thinking && m.id === lastId} liveState={liveState} />
      ))}
      {thinking && (
        <div className="flex gap-3 fade-in-up">
          <StealthRobot size={46} state="thinking" testid="thinking-robot" className="-mt-1" />
          <div className="text-sm text-slate-500 mt-2" data-testid="thinking-indicator">{assistant === "sam" ? "Sam" : "Anna"} is thinking…</div>
        </div>
      )}
    </div>
  );
}
